/**
 * Channel brief — opened from the VCR title.
 * Copy follows the actual synthesis and controls, not the one-line menu blurb.
 */

const GUIDES = {
  bounce: {
    lead: 'A physics instrument. Circles carry pitches, and collisions are the score.',
    body: [
      'Matter.js runs a 2D world. Each ball is born with a note from the current scale — major, minor, pentatonic, chromatic, or mixolydian — and a velocity. Static organic polygons, ten-vertex blobs, sit in the field and slowly spin. When a ball hits a shape, that ball’s note fires.',
      'The voice is a DuoSynth: two sine oscillators at a harmonicity of 1.5, with long attacks so the note blooms rather than clicks. It runs through the shared haunting chain — light cassette saturation, tape wow, a short chorus doubler, a low-pass, a dotted delay, and a long reverb. The page wash takes the color mapped to that note.',
      'Changing the scale does not rewrite balls already in the air. Only new balls pick from the new set.',
    ],
    play: [
      'START starts the engine and drops balls. + and − add or remove them. SCALE chooses the pitch set for the next ball.',
    ],
  },
  winter: {
    lead: 'A snowfall sequencer after the norns patch Fall. Landing is the note-on.',
    body: [
      'Light flakes (up to 14) and heavy flakes (up to 5) fall under gravity 0.32 with a wind of 0.34. A flake that comes to rest triggers a voice panned by its x position. Heavy flakes prefer a sine; light ones a triangle. Attack is 1.5 seconds, release 4.5. The fall bus is softened by a 5200 Hz low-pass before the haunting chain.',
      'On the horizon, a Three.js crystal pulses over a 20–60 second period. That size is the drone: cutoff follows the pulse, so the sun is the slow harmony and the snow is the melody. Modes are pentatonic, major, minor, dorian, lydian, mixolydian, and phrygian.',
    ],
    play: [
      'START. SCALE sets the mode. SPEED sets the sun’s pulse period, shown in seconds.',
    ],
  },
  scan: {
    lead: 'A radio looper in the lineage of Barcode. The stream is only the source; the grain is the piece.',
    body: [
      'Tune a live station — mostly SomaFM, plus a few public broadcasts — and record up to 10 seconds into a buffer. Six looping voices then read that buffer at independent rates (⅛× through 4×), levels, and pans. Each voice is drawn as its own colored waveform cell. Playback is six windows into the same recording, not six copies of the radio.',
    ],
    play: [
      'START, then tune. Capture writes the loop. Drag a voice cell to set its level and pan. RETUNE replaces the buffer; ADD MORE extends it.',
    ],
  },
  drippy: {
    lead: 'Weather as a drone. Ripples are events; the low voice is the weather under them.',
    body: [
      'Three mist layers drift at different speeds. Ripples, bubbles, and wisps spawn across a horizon. A drip voice plays a high set — MIDI 60, 64, 67, 71, 74, 72, 76, 79, 83 — with a short minimum gap. A drone voice plays a lower set — 43, 47, 50, 54, 40, 45 — more sparsely, each note held about 4.5 seconds.',
      'Horizontal position becomes stereo pan. Chorus and a shimmer reverb sit on the haunting chain, so the room is as much the instrument as the notes.',
    ],
    play: [
      'START. LPF, RVB DCY, RVB WET, and DLY FBK shape the room. The picture keeps raining either way.',
    ],
  },
  circles: {
    lead: 'Ten concentric rings, each a step of a C pentatonic built up from MIDI 48. Outer rings sit lower.',
    body: [
      'Moving the pointer plucks the ring under the cursor on a PluckSynth and sets that ring vibrating. Distance from the last point sets velocity. After a few seconds of stillness an LFO takes the rings and plays them itself. A pink-noise bed sits under everything; its level follows pointer height. The wash follows the note.',
    ],
    play: [
      'START, then move. Leave it alone and it keeps going. LPF, RVB DCY, RVB WET, and DLY FBK shape the tail.',
    ],
  },
  grid: {
    lead: 'A 3×3 of rotating wireframe glyphs. Each cell is a voice.',
    body: [
      'Hovering or touching a cell plays that cell’s note from the current scale — pentatonic, minor, or lydian. Voices are a mix of pluck, membrane, and sustained synths, each with its own delay, chorus, and pan into a shared reverb. After idle, an auto walk steps through the cells. REGENERATE rebuilds the nine forms; the notes stay with the cells.',
    ],
    play: [
      'START, then move across the grid. SCALE sets the nine pitches. REGENERATE replaces the glyphs.',
    ],
  },
  agnes: {
    lead: 'An infinite color field, named for Agnes Martin. Position is pitch.',
    body: [
      'Cells scroll past. Each is assigned a MIDI note from the selected scale, mapped across the field. Entering a cell strikes a bell — a short-attack PolySynth, rate-limited so a sweep does not machine-gun. Under it, a drone drawn from MIDI 24, 31, 36, and 43 holds for 15 seconds, filtered. The DRONE track is exponential: cutoff is 60 × 10^position, so the left of the bar is a rumble and the right opens the field.',
    ],
    play: [
      'START, then move across the cells. SCALE includes chromatic. Hold DRONE and drag to set how open the low voice is.',
    ],
  },
  tomboy: {
    lead: 'An OP-1 tombola. Objects tumble in a spinning cylinder, and hits are the sequencer.',
    body: [
      'Each object is born with a note from a pool rebuilt out of the current scale and register. Wall hits and neighbor hits trigger that note; the wash follows it. Pink and white noise sit in the voice so the barrel has grit, not just pitched events. Gravity can sit near zero, so objects drift rather than fall. SPIN is signed: negative reverses the barrel.',
    ],
    play: [
      'START. + and − add or remove objects. Drag the barrel to shove them. SPIN sets the rotation, from −.018 to .018.',
    ],
  },
  gogia: {
    lead: 'Two loops, after the norns patch Awake. The machine keeps rewriting itself.',
    body: [
      'Each step holds a scale degree, 1–8. On every cycle the drift rules fire: notes mutate every N cycles, lengths rewrite, the pattern rewrites on a longer interval, and the sound itself changes. Loop I and loop II have independent lengths. Bass and pad are separate layers, each with its own probability and mute.',
      'STEP edits a single value. LOOP, SOUND, and OPT expose length, cutoff, gain, delay, and the drift intervals. DICE reseeds. DRIFT is the thing that keeps the loops from becoming a loop.',
    ],
    play: [
      'START, then PLAY. DICE to reseed, DRIFT to keep rewriting. BPM, DIV, SCALE, and PROB set the clock. LEN I / LEN II set the two patterns.',
    ],
  },
  cruise: {
    lead: 'A mode-7 desert highway that never ends. The road is the sequencer.',
    body: [
      'Camera Z advances with speed. Crossings and stars fire notes as they approach. The melody is mostly stepwise — ±1 or ±2, rarely ±3 — with the occasional open fifth, a line walking the horizon rather than leaping it. Stars take small voicings and sometimes lift a note an octave. SCALE includes mystery, dorian, phrygian, pentatonic, and lydian.',
    ],
    play: [
      'START, then let it drive. SCALE sets the road’s pitch set.',
    ],
  },
  luna: {
    lead: 'A moon whose phase is the score. The terminator is the clock.',
    body: [
      'A directional light orbits. Phase angle sets which face is lit; dragging horizontally scrubs that angle, and SPEED keeps it turning when you let go. Four hundred twenty stars sit in the sky. Impacts on the surface flash and ring, and pick a voice — FM bell, pluck, metal, membrane, saw chorus, sine pad, AM shimmer, duo bell, or shimmer bell — colored to match. Root is MIDI 50. SCALE is pentatonic, minor, or lydian.',
    ],
    play: [
      'START. Drag left and right to scrub the phase. SPEED sets the free run. RVB is the sky.',
    ],
  },
  murmur: {
    lead: 'A boid flock. Zones in the field are resonant drones that wake when the flock occupies them.',
    body: [
      'Separation, alignment, and cohesion run on a spatial hash — neighbor radius 70, cell size the same. Count scales with the viewport, clamped between 32 and 300. Five zones sit in the picture. Occupancy of a zone holds a filtered drone at that zone’s scale degree (root MIDI 48) and pans with the zone. Crossing into a zone plucks. A shark occasionally cuts through; a click, or a rare automatic scatter, throws the flock and opens a short bandpassed noise burst.',
    ],
    play: [
      'START. SCALE sets the zone pitches. SPEED, COHESION, and DENSITY reshape the flock. Click the field to scatter them.',
    ],
  },
  train: {
    lead: 'A pixel city at night. Passing trains are a voice that pans across the stereo field.',
    body: [
      'The picture is drawn at a logical width of 320 and parallaxed — background at 0.3, foreground at 0.85. Windows are degrees of the current key and mode. Underneath, stacked voices drone: a bass and duo synths, through a small VHS chain — wow, light Chebyshev saturation, flutter. When a train passes, a voice travels with it. Keys are C, E♭, F, A♭, B♭. Modes run from dorian and phrygian through the pentatonics.',
    ],
    play: [
      'START. KEY and MODE set the pitch set. DENSITY fills the windows. REVERB sets how far the night sits.',
    ],
  },
  stoch: {
    lead: 'Stochastic clouds after Xenakis, plus a GENDYN worklet. Events are drawn, not sequenced.',
    body: [
      'Wait times are Poisson: the gap until the next note is −ln(U) / density. Pitch is drawn from a Gaussian or a Cauchy around a center, then quantized to the scale — pentatonic, major, minor, whole-tone, or chromatic — between MIDI 36 and 96. Each event is a particle. SPRD is the width of that draw; CTR shifts the center.',
      'The GENDYN voice is dynamic stochastic synthesis. A breakpoint waveform — twelve segments — random-walks its amplitudes and durations by a Gaussian. SIGMA is the size of that walk. Larger sigma, less periodic, closer to noise. PLUCK is a separate attack rate on top of the clouds.',
    ],
    play: [
      'START. DENS sets how often events arrive. DIST chooses Gauss or Cauchy. GNDYN and SIGMA set the stochastic waveform. SCALE quantizes what the clouds are allowed to say.',
    ],
  },
  about: {
    lead: 'Not an instrument. A credits reel over a looping video.',
    body: [
      'The reel lists each channel, what it is indebted to — Barcode, Fall, Awake — and the tools the table is built on: Tone.js, Three.js, Matter.js, p5, SomaFM. It scrolls for as long as you leave it up. This title is the index to that reel; the channel itself is the long version.',
    ],
    play: [
      'Let it run. Nothing here is playable except the reading.',
    ],
  },
};

function padCh(n) {
  return String(n).padStart(2, '0');
}

function createStatic(canvas) {
  const ctx = canvas.getContext('2d', { alpha: false });
  const POOL = 6;
  let frames = [];
  let idx = 0;
  let raf = 0;
  let running = false;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function bake() {
    const w = Math.max(2, Math.ceil(window.innerWidth / 3));
    const h = Math.max(2, Math.ceil(window.innerHeight / 3));
    canvas.width = w;
    canvas.height = h;
    frames = [];
    const count = reduce.matches ? 1 : POOL;
    for (let f = 0; f < count; f++) {
      const img = ctx.createImageData(w, h);
      const d = img.data;
      for (let i = 0; i < d.length; i += 4) {
        const v = (Math.random() * 255) | 0;
        d[i] = d[i + 1] = d[i + 2] = v;
        d[i + 3] = 255;
      }
      frames.push(img);
    }
    idx = 0;
  }

  function draw() {
    if (!frames.length) return;
    ctx.putImageData(frames[idx], 0, 0);
    idx = (idx + 1) % frames.length;
  }

  function loop() {
    draw();
    if (reduce.matches) return;
    raf = requestAnimationFrame(loop);
  }

  function onResize() {
    if (!running) return;
    bake();
    draw();
  }

  return {
    start() {
      if (running) return;
      running = true;
      bake();
      loop();
      window.addEventListener('resize', onResize);
    },
    stop() {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    },
  };
}

function fillCopy(body, guide) {
  body.replaceChildren();

  const lead = document.createElement('p');
  lead.className = 'nt-ch-info__lead';
  lead.textContent = guide.lead;
  body.appendChild(lead);

  guide.body.forEach((text) => {
    const p = document.createElement('p');
    p.textContent = text;
    body.appendChild(p);
  });

  const playHead = document.createElement('p');
  playHead.className = 'nt-ch-info__label';
  playHead.textContent = 'Playing it';
  body.appendChild(playHead);

  guide.play.forEach((text) => {
    const p = document.createElement('p');
    p.className = 'nt-ch-info__play';
    p.textContent = text;
    body.appendChild(p);
  });
}

export function createChannelInfo({ onClose } = {}) {
  const root = document.createElement('div');
  root.className = 'nt-ch-info';
  root.hidden = true;

  const staticCanvas = document.createElement('canvas');
  staticCanvas.className = 'nt-ch-info__static';
  staticCanvas.setAttribute('aria-hidden', 'true');

  const panel = document.createElement('div');
  panel.className = 'nt-ch-info__panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-labelledby', 'nt-ch-info-title');

  panel.innerHTML = `
    <header class="nt-ch-info__head">
      <h2 class="nt-ch-info__name" id="nt-ch-info-title"></h2>
      <button type="button" class="nt-ch-info__close" data-close>EXIT</button>
    </header>
    <div class="nt-ch-info__scroll" tabindex="0">
      <p class="nt-ch-info__ch"></p>
      <div class="nt-ch-info__body"></div>
    </div>
  `;

  root.append(staticCanvas, panel);
  document.body.appendChild(root);

  const staticFx = createStatic(staticCanvas);
  const nameEl = panel.querySelector('.nt-ch-info__name');
  const chEl = panel.querySelector('.nt-ch-info__ch');
  const bodyEl = panel.querySelector('.nt-ch-info__body');
  const closeBtn = panel.querySelector('[data-close]');
  const scrollEl = panel.querySelector('.nt-ch-info__scroll');

  let open = false;
  let returnFocus = null;
  let closeTimer = null;

  function onKeydown(e) {
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = [closeBtn, scrollEl].filter((el) => el && !el.hasAttribute('disabled'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onPointerDown(e) {
    if (!open) return;
    if (e.target === root || e.target === staticCanvas) close();
  }

  function render(project) {
    const label = (project.vcrLabel || project.name || 'Channel').toUpperCase();
    const guide = GUIDES[project.id] || {
      lead: project.desc || 'A Noise Table channel.',
      body: [],
      play: [],
    };
    nameEl.textContent = label;
    chEl.textContent = project.ch ? `CH ${padCh(project.ch)}` : '';
    fillCopy(bodyEl, guide);
    scrollEl.scrollTop = 0;
  }

  function openInfo(project, trigger) {
    if (!project || open) return;
    render(project);
    returnFocus = trigger || document.activeElement;
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    staticFx.start();
    requestAnimationFrame(() => {
      root.classList.add('is-open');
    });
    open = true;
    document.addEventListener('keydown', onKeydown, true);
    closeBtn.focus();
  }

  function close() {
    if (!open) return;
    open = false;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    staticFx.stop();
    document.removeEventListener('keydown', onKeydown, true);
    closeTimer = setTimeout(() => {
      if (!open) root.hidden = true;
    }, 220);
    const back = returnFocus;
    returnFocus = null;
    onClose?.();
    if (back && typeof back.focus === 'function') back.focus();
  }

  root.addEventListener('pointerdown', onPointerDown);
  closeBtn.addEventListener('click', close);
  panel.addEventListener('pointerdown', (e) => e.stopPropagation());

  return {
    open: openInfo,
    close,
    isOpen: () => open,
  };
}
